import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, SettingIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { createHowDidYouObtainLECBrochure } from '../../api/how-did-you-obtain-lec-brochures/create-how-did-you-obtain-lec-brochure'
import { updateHowDidYouObtainLecBrochure } from '../../api/how-did-you-obtain-lec-brochures/update-how-did-you-obtain-lec-brochure'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import UserNavBar from '../../components/elements/nav-bar'
import HowDidYouObtainLecBrochuresForm from '../../components/pages/how-did-you-obtain-lec-brochures/how-did-you-obtain-lec-brochures-form'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { howDidYouObtainLECBrochureFields } from '../../lib/types/fields/how-did-you-obtain-lec-brochures/how-did-you-obtain-lec-brochures'
import { HowDidYouObtainLecBrochure } from '../../lib/types/models/how-did-you-obtain-lec-brochures/how-did-you-obtain-lec-brochure'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

const HowDidYouObtainLecBrochurePage = () => {
  const tableRef = useRef<DynamicTableRef<HowDidYouObtainLecBrochure>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'edit' | 'create',
    HowDidYouObtainLecBrochure
  >(['edit', 'create'])

  const updateHowDidYouObtainLecBrochureMutation = useMutation({
    mutationFn: updateHowDidYouObtainLecBrochure,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Comment avez-vous obtenu la brochure Lec modifié avec succès')
    }
  })

  const createHowDidYouObtainLecBrochure = useMutation({
    mutationFn: createHowDidYouObtainLECBrochure,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Comment avez-vous obtenu la brochure Lec créé avec succès')
    }
  })

  const columns: Column<HowDidYouObtainLecBrochure>[] = [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true,
      cell: (data) => data.row.original.id
    },
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true,
      cell: (data) => data.row.original.name
    },

    {
      accessorKey: 'id',
      header: '',
      cell: (data) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('edit', data.row.original)}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Comment avez-vous obtenu la brochure Lec" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<HowDidYouObtainLecBrochure>
        ref={tableRef}
        title="Comment avez-vous obtenu la brochure Lec"
        columns={columns}
        deleteMessage={{ name: 'Comment avez-vous obtenu la brochure Lec', attributeName: 'name' }}
        selectable
        allowSearchParam
        extraParams={{ fields: howDidYouObtainLECBrochureFields }}
        endpoint={API.howDidYouObtainLECBrochure.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer',
            fn: () => toggleModal('create')
          }
        ]}
      />

      {/* Edit */}
      {selectedData && (
        <Modal
          icon={SettingIcon}
          isOpen={modalState.edit}
          title="Modifier Comment avez-vous obtenu la brochure Lec"
          hideActions
          onCancel={() => toggleModal('edit')}
        >
          <HowDidYouObtainLecBrochuresForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              isLoading: updateHowDidYouObtainLecBrochureMutation.isPending,
              onConfirm: async (values) => {
                await updateHowDidYouObtainLecBrochureMutation.mutateAsync({
                  id: selectedData.id,
                  ...values
                })
              }
            })}
          />
        </Modal>
      )}

      {/* Create */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.create}
        title="Créer Comment avez-vous obtenu la brochure Lec"
        hideActions
        onCancel={() => toggleModal('create')}
      >
        <HowDidYouObtainLecBrochuresForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            isLoading: createHowDidYouObtainLecBrochure.isPending,
            onConfirm: async (values) => {
              await createHowDidYouObtainLecBrochure.mutateAsync(values)
            }
          })}
        />
      </Modal>
    </div>
  )
}

export default HowDidYouObtainLecBrochurePage
